export interface ToolItem {
  name: string;
  path: string;
  description: string;
}

export interface ToolCategory {
  text: string;
  iconName: string;
  items: ToolItem[];
}

export const toolCategories: ToolCategory[] = [
  {
    text: 'Address & ENS',
    iconName: 'AccountBalanceWallet',
    items: [
      { name: 'ENS Lookup', path: '/ens-lookup', description: 'Resolve ENS names to Ethereum addresses and reverse lookup' },
      { name: 'Address Validator', path: '/address-validator', description: 'Validate Ethereum addresses with checksum verification' },
      { name: 'Balance Checker', path: '/balance-checker', description: 'Check ETH and token balances for any address' },
    ],
  },
  {
    text: 'Smart Contracts',
    iconName: 'Code',
    items: [
      { name: 'ABI Encoder/Decoder', path: '/abi-encoder-decoder', description: 'Encode and decode smart contract ABI function calls' },
      { name: 'Contract Verification Helper', path: '/contract-verification-helper', description: 'Prepare contracts for Etherscan verification' },
      { name: 'Contract Read/Write UI', path: '/contract-read-write', description: 'Interact with smart contracts directly from your browser' },
    ],
  },
  {
    text: 'Converters',
    iconName: 'Calculate',
    items: [
      { name: 'Ether Unit Converter', path: '/eth-unit-converter', description: 'Convert between Wei, Gwei, and ETH instantly' },
      { name: 'Token Unit Converter', path: '/token-conversion', description: 'Convert token amounts with custom decimal places' },
      { name: 'Unix/UTC Time Converter', path: '/epoch-conversion', description: 'Convert Unix timestamps to human-readable dates' },
      { name: 'Number Base Converter', path: '/base-conversion', description: 'Convert between decimal, hex, binary, and octal' },
      { name: 'String \u2194 Bytes32 Converter', path: '/string-bytes32', description: 'Convert strings to Solidity bytes32 format' },
      { name: 'URL Encoder & Decoder', path: '/url-encoder', description: 'Encode and decode URL components' },
      { name: 'Ethereum Block Time Converter', path: '/eth-block-time', description: 'Convert block numbers to timestamps and vice versa' },
    ],
  },
  {
    text: 'Decoders',
    iconName: 'Code',
    items: [
      { name: 'Calldata Decoder', path: '/calldata-decoder', description: 'Decode raw Ethereum transaction calldata' },
      { name: 'Event Log Decoder', path: '/event-decoder', description: 'Decode Ethereum event logs from transaction receipts' },
      { name: 'JWT Decoder', path: '/jwt-decoder', description: 'Decode and inspect JSON Web Tokens' },
      { name: 'Ethereum Transaction Decoder', path: '/transaction-decoder', description: 'Decode and analyze Ethereum transactions' },
    ],
  },
  {
    text: 'Encoders',
    iconName: 'Build',
    items: [
      { name: 'Contract Constructor Encoder', path: '/constructor-encoder', description: 'Encode constructor parameters for contract deployment' },
    ],
  },
  {
    text: 'Generators',
    iconName: 'Settings',
    items: [
      { name: 'Ethereum Vanity Address Generator', path: '/vanity-address', description: 'Generate custom Ethereum addresses with chosen prefixes' },
      { name: 'UUID Generator', path: '/uuid-generator', description: 'Generate UUIDs in v1, v4, and v5 formats' },
      { name: 'Hash Generator', path: '/hash-generator', description: 'Generate MD5, SHA-1, SHA-256, and Keccak-256 hashes' },
      { name: 'QR Code Generator', path: '/qr-generator', description: 'Create QR codes for addresses, URLs, and data' },
      { name: 'Random Data Generator', path: '/random-generator', description: 'Generate random test data for development' },
      { name: 'Random Text Generator', path: '/random-text-generator', description: 'Generate Lorem Ipsum and placeholder text' },
    ],
  },
  {
    text: 'Formatters',
    iconName: 'FormatPaint',
    items: [
      { name: 'JSON Formatter', path: '/json-formatter', description: 'Format, validate, and beautify JSON data' },
      { name: 'Code Minifier & Beautifier', path: '/minifier', description: 'Minify or beautify JavaScript, CSS, and HTML' },
    ],
  },
  {
    text: 'Testers',
    iconName: 'BugReport',
    items: [
      { name: 'Regex Tester', path: '/regex-tester', description: 'Test and debug regular expressions with live matching' },
      { name: 'HTTP Request Builder', path: '/http-builder', description: 'Build and test HTTP API requests' },
    ],
  },
  {
    text: 'Utilities',
    iconName: 'EmojiEmotions',
    items: [
      { name: 'Color Picker', path: '/color-picker', description: 'Pick colors and convert between HEX, RGB, and HSL' },
      { name: 'Clipboard Inspector', path: '/clipboard-inspector', description: 'Inspect and analyze clipboard contents' },
      { name: 'Emoji Picker', path: '/emoji-picker', description: 'Browse and copy emojis by category' },
    ],
  },
  {
    text: 'Gas & Price Tracker',
    iconName: 'TrendingUp',
    items: [
      { name: 'Gas Price Tracker', path: '/gas-price-tracker', description: 'Monitor real-time Ethereum gas prices across chains' },
    ],
  },
  {
    text: 'MEV & Trading Tools',
    iconName: 'Build',
    items: [
      { name: 'MEV Bot Simulator', path: '/mev-bot-builder', description: 'Simulate MEV strategies with real market data' },
    ],
  },
  {
    text: 'Security Tools',
    iconName: 'Security',
    items: [
      { name: 'Contract Security Scanner', path: '/contract-security-scanner', description: 'Scan Solidity contracts for common vulnerabilities' },
    ],
  },
];

export const allTools: ToolItem[] = toolCategories.flatMap((cat) => cat.items);

export function findToolCategory(path: string): string | undefined {
  for (const cat of toolCategories) {
    if (cat.items.some((item) => item.path === path)) {
      return cat.text;
    }
  }
  return undefined;
}

export function findTool(path: string): ToolItem | undefined {
  return allTools.find((t) => t.path === path);
}
